import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Download, CircleAlert, Lock } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from 'recharts';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMe } from '@/features/me/useMe';
import { metricsApi, type MetricsReport } from '@/features/metrics/metricsApi';
import { ApiError } from '@/lib/apiClient';

// Theme-friendly palette (concrete hex so SVG fills are reliable across browsers).
const COLORS = {
  primary: '#347eff',
  emerald: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  zinc: '#a1a1aa',
  purple: '#a855f7'
};
const axisColor = 'hsl(var(--muted-foreground))';
const gridColor = 'hsl(var(--border))';

/** yyyy-MM-dd for an offset-from-today date, in local time. */
function isoDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/** Local yyyy-MM-dd → UTC instant at local midnight. */
function dayStartUtc(localDate: string): string {
  return new Date(`${localDate}T00:00:00`).toISOString();
}

function fmtDuration(hours: number | null): string {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function fmtPct(pct: number | null): string {
  return pct == null ? '—' : `${pct.toFixed(0)}%`;
}

/**
 * /settings/reports — IT-Admin ticket metrics over a date range, with charts +
 * CSV export. SLA targets are fixed business rules (Urgent 1h, Normal 1 biz day,
 * else 3 biz days); the backend classifies each ticket's first-response state.
 */
export function Reports() {
  const getAccessToken = useAccessToken();
  const me = useMe();
  const isAdmin = me.data?.role === 'ITAdmin';

  const [fromDate, setFromDate] = useState(isoDate(30));
  const [toDate, setToDate] = useState(isoDate(0));

  // Range: [from 00:00 local, (to + 1 day) 00:00 local) so the end date is inclusive.
  const fromUtc = dayStartUtc(fromDate);
  const toExclusive = useMemo(() => {
    const d = new Date(`${toDate}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  }, [toDate]);

  const report = useQuery({
    queryKey: ['metrics', fromUtc, toExclusive],
    queryFn: ({ signal }) => metricsApi.get(fromUtc, toExclusive, getAccessToken, signal),
    enabled: isAdmin
  });

  const setPreset = (days: number) => {
    setFromDate(isoDate(days));
    setToDate(isoDate(0));
  };

  const forbidden = (report.error as ApiError)?.status === 403;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" /> Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Ticket metrics &amp; SLA performance for a date range.</p>
        </div>
        {report.data && (
          <button
            type="button"
            onClick={() => exportCsv(report.data!, fromDate, toDate)}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm hover:bg-muted"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </header>

      {!isAdmin && me.isSuccess && (
        <EmptyState icon={<Lock className="w-6 h-6" />} title="IT-Admin access required"
          body="Reports are only available to IT administrators." />
      )}

      {isAdmin && (
        <>
          {/* Date range controls */}
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground mb-1">From</span>
              <input type="date" value={fromDate} max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="rounded-md border bg-card px-3 py-2 text-sm" />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground mb-1">To</span>
              <input type="date" value={toDate} min={fromDate} max={isoDate(0)}
                onChange={(e) => setToDate(e.target.value)}
                className="rounded-md border bg-card px-3 py-2 text-sm" />
            </label>
            <div className="flex items-center gap-1.5">
              {[
                { label: '7d', days: 7 },
                { label: '14d', days: 14 },
                { label: '30d', days: 30 },
                { label: '90d', days: 90 }
              ].map((p) => (
                <button key={p.label} type="button" onClick={() => setPreset(p.days)}
                  className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-muted">
                  Last {p.label}
                </button>
              ))}
            </div>
          </div>

          {report.isPending && <div className="text-sm text-muted-foreground">Loading metrics…</div>}

          {forbidden && (
            <EmptyState icon={<Lock className="w-6 h-6" />} title="IT-Admin access required"
              body="Reports are only available to IT administrators." />
          )}

          {report.isError && !forbidden && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
              <CircleAlert className="w-4 h-4 mt-0.5" />
              <span>Couldn't load metrics: {(report.error as Error).message}</span>
            </div>
          )}

          {report.data && <ReportBody report={report.data} />}
        </>
      )}
    </div>
  );
}

function ReportBody({ report }: { report: MetricsReport }) {
  const s = report.summary;

  // Pretty short date for the daily axis.
  const daily = report.daily.map((d) => ({ ...d, label: d.date.slice(5) /* MM-dd */ }));

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Created" value={s.created.toString()} />
        <Kpi label="Resolved" value={s.resolved.toString()} />
        <Kpi label="Open now" value={s.openAtEnd.toString()} />
        <Kpi label="Avg 1st response" value={fmtDuration(s.avgFirstResponseHours)} />
        <Kpi label="Avg resolution" value={fmtDuration(s.avgResolutionHours)} />
        <Kpi label="SLA compliance" value={fmtPct(s.slaCompliancePct)}
          hint={`${s.slaMet} met · ${s.slaBreached} missed${s.slaPending ? ` · ${s.slaPending} pending` : ''}`} />
      </div>

      {/* Daily volume */}
      <Card title="Ticket volume — created vs. resolved">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={daily} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="created" name="Created" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.18} />
            <Area type="monotone" dataKey="resolved" name="Resolved" stroke={COLORS.emerald} fill={COLORS.emerald} fillOpacity={0.18} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* SLA by priority */}
        <Card title="First-response SLA by priority">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={report.byPriority} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="priority" tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="slaMet" name="Met" stackId="s" fill={COLORS.emerald} />
              <Bar dataKey="slaBreached" name="Missed" stackId="s" fill={COLORS.red} />
              <Bar dataKey="slaPending" name="Pending" stackId="s" fill={COLORS.zinc} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr><th className="py-1 pr-3">Priority</th><th className="pr-3">Target</th><th className="pr-3">Tickets</th><th>Compliance</th></tr>
              </thead>
              <tbody>
                {report.byPriority.map((p) => (
                  <tr key={p.priority} className="border-t border-border">
                    <td className="py-1 pr-3 font-medium">{p.priority}</td>
                    <td className="pr-3 text-muted-foreground">{p.target}</td>
                    <td className="pr-3">{p.count}</td>
                    <td>{fmtPct(p.slaCompliancePct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* By category */}
        <Card title="Volume by category">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={report.byCategory} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="category" tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Tickets" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr><th className="py-1 pr-3">Category</th><th className="pr-3">Tickets</th><th>Avg resolution</th></tr>
              </thead>
              <tbody>
                {report.byCategory.map((c) => (
                  <tr key={c.category} className="border-t border-border">
                    <td className="py-1 pr-3 font-medium">{c.category}</td>
                    <td className="pr-3">{c.count}</td>
                    <td>{fmtDuration(c.avgResolutionHours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Aging (snapshot) */}
        <Card title="Open ticket aging (current)" subtitle="Snapshot of all open tickets right now — independent of the date range.">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={report.openAging} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} stroke={gridColor} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Open tickets" radius={[4, 4, 0, 0]}>
                {report.openAging.map((_, i) => (
                  <Cell key={i} fill={[COLORS.emerald, COLORS.primary, COLORS.amber, COLORS.red][i] ?? COLORS.zinc} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* By assignee */}
        <Card title="Workload by assignee">
          {report.byAssignee.length === 0 ? (
            <p className="text-sm text-muted-foreground">No assigned tickets in this range.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="py-2 pr-3">Assignee</th><th className="pr-3">Open</th><th className="pr-3">Resolved</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {report.byAssignee.map((a) => (
                    <tr key={a.assignee} className="border-t border-border">
                      <td className="py-2 pr-3 font-medium">{a.assignee}</td>
                      <td className="pr-3">{a.open}</td>
                      <td className="pr-3">{a.resolved}</td>
                      <td>{a.open + a.resolved}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const tooltipStyle = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  fontSize: 12,
  color: 'hsl(var(--foreground))'
};

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-muted-foreground leading-snug">{hint}</div>}
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="mb-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed bg-card p-10 text-center">
      <div className="mx-auto text-muted-foreground mb-2 w-fit">{icon}</div>
      <div className="text-base font-medium">{title}</div>
      <p className="text-sm text-muted-foreground mt-1.5">{body}</p>
    </div>
  );
}

/** Build a multi-section CSV and trigger a download. Kept dependency-free. */
function exportCsv(r: MetricsReport, fromDate: string, toDate: string) {
  const esc = (v: string | number | null) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows: string[] = [];
  rows.push(`AES Helpdesk metrics,${fromDate} to ${toDate}`);
  rows.push('');
  rows.push('Summary');
  rows.push('Metric,Value');
  rows.push(`Created,${r.summary.created}`);
  rows.push(`Resolved,${r.summary.resolved}`);
  rows.push(`Open now,${r.summary.openAtEnd}`);
  rows.push(`Avg first response (hours),${r.summary.avgFirstResponseHours ?? ''}`);
  rows.push(`Avg resolution (hours),${r.summary.avgResolutionHours ?? ''}`);
  rows.push(`SLA met,${r.summary.slaMet}`);
  rows.push(`SLA missed,${r.summary.slaBreached}`);
  rows.push(`SLA pending,${r.summary.slaPending}`);
  rows.push(`SLA compliance %,${r.summary.slaCompliancePct ?? ''}`);
  rows.push('');
  rows.push('By priority');
  rows.push('Priority,Target,Tickets,Met,Missed,Pending,Compliance %');
  r.byPriority.forEach((p) =>
    rows.push([p.priority, p.target, p.count, p.slaMet, p.slaBreached, p.slaPending, p.slaCompliancePct ?? ''].map(esc).join(',')));
  rows.push('');
  rows.push('By category');
  rows.push('Category,Tickets,Avg resolution (hours)');
  r.byCategory.forEach((c) => rows.push([c.category, c.count, c.avgResolutionHours ?? ''].map(esc).join(',')));
  rows.push('');
  rows.push('By assignee');
  rows.push('Assignee,Open,Resolved');
  r.byAssignee.forEach((a) => rows.push([a.assignee, a.open, a.resolved].map(esc).join(',')));
  rows.push('');
  rows.push('Open aging (current snapshot)');
  rows.push('Bucket,Open tickets');
  r.openAging.forEach((b) => rows.push([b.bucket, b.count].map(esc).join(',')));
  rows.push('');
  rows.push('Daily volume');
  rows.push('Date,Created,Resolved');
  r.daily.forEach((d) => rows.push([d.date, d.created, d.resolved].map(esc).join(',')));

  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `aes-helpdesk-metrics_${fromDate}_to_${toDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
